import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from '../routing';
import { documentsApi, suppliersApi, catalogApi, creditsApi, paymentsApi, formatCurrency } from '../utils/api';
import { toast } from '../components/Toast';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../utils/useConfirm';
import { confirmAddRecipientAlias, confirmIgnoreRecipientBid } from '../utils/recipientAlias';
import { markSupplierDeliveryNotes } from '../utils/markSupplierDeliveryNotes';
import { useSettings } from '../context/SettingsContext';
import { isImplausibleDateYear } from '../utils/dateSanity';
import { track, EVENTS } from '../utils/analytics';
import LinkDeliveryNotesModal from '../components/LinkDeliveryNotesModal';
import LinkOpenCreditsModal from '../components/LinkOpenCreditsModal';
import ReviewDocPane from '../components/ReviewDocPane';
import ReviewIssueSummary from '../components/ReviewIssueSummary';
import SearchableSelect from '../components/SearchableSelect';
import { calcNetUnitPrice, calcLineTotal, calcCreditTotal, roundDocTotal } from '../utils/lineCalc';
import { round2 } from '../utils/money';
import { buildReview } from '../utils/reviewTiming';
import { detectVatRule } from '../utils/vatRule';
import { isDupExemptDocType, isPayableDocType } from '../utils/docTypes';
import { rowMismatched, analyzeReviewRow, invoiceDiscountMismatch, deriveReviewIssues } from '../utils/reviewIssues';
import { useOpenDeliveryNotes, openNotesRange, rangeForInvoice } from '../utils/useOpenDeliveryNotes';
import { PAYMENT_METHOD_OPTIONS } from './SupplierManagementPage';
import Icon from '../components/Icon';
import NewSupplierOptions, { hasUnmatchedSupplierName } from '../components/NewSupplierOptions';
import { normName } from '../utils/normName';
import PriceSourceBadge from '../components/PriceSourceBadge';

const CREDIT_DOC_TYPES = ['חשבונית זיכוי', 'תעודת החזרה'];

const DOC_TYPES = ['חשבונית', 'חשבונית זיכוי', 'חשבונית מרכזת', 'תעודת משלוח', 'תעודת החזרה', 'חשבונית/קבלה', 'קבלה', 'חשבון עסקה', 'כרטסת', 'אחר'];
// Stable option arrays for the searchable selects (module scope → stable identity).
const DOC_TYPE_OPTIONS = DOC_TYPES.map(t => ({ value: t, label: t }));
const STATUS_OPTIONS = ['טיוטה', 'תקין', 'לא תקין'].map(s => ({ value: s, label: s }));
// Same vocabulary as DocumentViewPage's own "שולם" block on a draft's edit
// form — see there for the full reasoning. Ported here (minus the "אשר
// לתשלום לחודש" sub-offer, which needs the document's existing supplier
// lifecycle and has no equivalent before the document exists) so a document
// saved straight from upload can be approved-and-paid in one step too,
// instead of always landing as an unapproved draft that needs a second trip
// through /drafts to reach the same control.
const PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS.map(o => o.value);
const todayISO = () => new Date().toISOString().split('T')[0];
const NO_PAY_ON_APPROVE = { checked: false, payment_method: '', payment_date: '' };
const PAY_NOTE_QUICK = 'הופיע שולם על המסמך';
// The invoice discount as the totals show it: −X off, or +X when the supplier
// rounded the total UP through the discount line — the reading gives that as
// a negative ₪ discount (OCR prompt rule, owner 31/10/2026).
const hasInvoiceDiscount = (amount) => Math.round(amount * 100) !== 0;
const signedDiscount = (amount) => `${amount > 0 ? '−' : '+'}${Math.abs(amount).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function UploadDocumentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId, isAdmin } = useAuth();
  const [askConfirm, confirmDialog] = useConfirm();
  const { vatRate, paymentTrackingEnabled } = useSettings();
  const fileInputRef = useRef();
  const bulkInputRef = useRef();
  // 'single' = today's behaviour (one doc, optional multi-page).
  // 'bulk' = admin-only batch upload where each file becomes its own draft
  // doc reviewed afterwards from /drafts.
  const [mode, setMode] = useState('single');
  const [bulkStaged, setBulkStaged] = useState([]); // { file, previewUrl }
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, running: false });
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  // `saving` drives the button UI. The actual re-entry guard is savingRef,
  // because setSaving(true) does NOT update `saving` synchronously — two clicks
  // in the same tick would both read the stale `false` and both POST. A draft
  // save is not idempotent (drafts are excluded from both the server duplicate
  // check and the partial unique index), so that produces two documents.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [filePath, setFilePath] = useState('');
  const [filePaths, setFilePaths] = useState([]);
  // {invoiceId, invoiceTotal, invoiceSubtotal, supplierName, invoiceStatus, existingLinkCount, afterClose}
  const [linkModal,         setLinkModal]         = useState(null);
  // Set when the "open delivery notes" chip retyped this invoice to a מרכזת:
  // the window that chip counted, so the link modal opens on those notes.
  const [convertRange, setConvertRange] = useState(null);
  const [openCreditsModal,  setOpenCreditsModal]  = useState(null);  // {creditDocId, creditDocTotal, supplierId, supplierName, afterClose}
  // "שולם" on save — see NO_PAY_ON_APPROVE above. `payNoteModal` holds `true`
  // while the "why is this paid" note is being collected (handleSave defers
  // there and re-enters once payNote is set); `payNote` is the note itself.
  const [payOnApprove, setPayOnApprove] = useState(NO_PAY_ON_APPROVE);
  const [payNoteModal, setPayNoteModal] = useState(false);
  const [payNote,      setPayNote]      = useState('');
  const [stagedFiles, setStagedFiles] = useState([]); // { file, previewUrl }
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    supplier_id: location.state?.supplierId || '',
    doc_type: 'חשבונית',
    doc_num: '',
    date: '',
    subtotal: '',
    vat: '',
    total: '',
    status: 'טיוטה',
    invoice_discount: 0,
    invoice_discount_type: 'percent',
  });
  const [items, setItems] = useState([]);
  // The delivery-note references a חשבונית מרכזת lists in its own body. Kept
  // out of `form` on purpose: nothing on the review screen edits it, and it is
  // a claim read off the page rather than a field the user is filling in.
  const [coveredDocs, setCoveredDocs] = useState([]);
  const [stage, setStage] = useState('upload'); // upload | review
  // When the review stage opened — reported with a save straight to
  // תקין/לא תקין as the review's duration (utils/reviewTiming.js).
  const reviewOpenedAtRef = useRef(null);
  useEffect(() => { reviewOpenedAtRef.current = stage === 'review' ? Date.now() : null; }, [stage]);
  // Local object URLs of the just-uploaded files for the side-by-side review
  // pane. The authed /api/files route only serves files that
  // belong to a SAVED document, and at review time the doc doesn't exist yet —
  // so the pane must show the local blobs. [{ url, isPdf }]
  const [reviewPreviews, setReviewPreviews] = useState([]);
  const reviewPreviewsRef = useRef([]);
  reviewPreviewsRef.current = reviewPreviews;

  // Analytics: accumulate which fields the user corrects on the AI-extracted
  // draft so ai_extraction_corrected can report *where* Claude was wrong without
  // sending any values. Reset per extraction. Field NAMES / counts only.
  const editedItemFieldsRef = useRef(new Set());
  const editedItemRowsRef = useRef(new Set());
  const itemsAddedRef = useRef(0);
  const itemsRemovedRef = useRef(0);
  const extractedItemCountRef = useRef(0);
  // Retry tracking: remember the last extract failure so the next attempt is
  // tagged as a recovery, and whether the user flipped the AI toggle before it.
  const extractFailedRef = useRef(null);
  const aiToggleAtFailureRef = useRef(true);
  const resetCorrectionTracking = useCallback((extractedCount) => {
    editedItemFieldsRef.current = new Set();
    editedItemRowsRef.current = new Set();
    itemsAddedRef.current = 0;
    itemsRemovedRef.current = 0;
    extractedItemCountRef.current = extractedCount;
  }, []);
  // Revoke preview URLs when leaving the review stage or unmounting.
  useEffect(() => {
    if (stage !== 'review') {
      setReviewPreviews(prev => {
        prev.forEach(p => URL.revokeObjectURL(p.url));
        return prev.length ? [] : prev;
      });
    }
  }, [stage]);
  useEffect(() => () => {
    reviewPreviewsRef.current.forEach(p => URL.revokeObjectURL(p.url));
  }, []);
  // newSupplierName holds the AI-extracted supplier name when no match was
  // found — it both drives the "לא נמצא ספק תואם" hint and pre-fills the
  // supplier combobox's inline "add supplier" row.
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierOptions, setNewSupplierOptions] = useState({ hq_only: false, category_line_id: '' });
  // The supplier's ח.פ. as printed on this upload (server-cleaned, never the
  // buyer's own) — "הוסף ספק חדש" creates the new supplier with it.
  const [printedSupplierBid, setPrintedSupplierBid] = useState('');
  // Recipient validation surfaced by the server. { match, recipient_name,
  // restaurant_name }. match values: 'match' | 'mismatch' | 'not_found' | null.
  const [recipientCheck, setRecipientCheck] = useState(null);
  const [openCredits, setOpenCredits] = useState(null);
  const [ocrRawText, setOcrRawText] = useState(''); // fallback when JSON parse fails
  // The AI's answer was cut at its output cap (#338): how many lines it read
  // before the cut, or null. The rest of the document is still to be typed.
  const [cutLineCount, setCutLineCount] = useState(null);
  const [vatMode, setVatMode] = useState('auto'); // 'auto' | 'manual'
  const [useAI,   setUseAI]   = useState(true);   // false = skip AI, just save file
  // Per-supplier rule learning: keep Claude's original extraction so we can
  // diff against the final saved form and detect known patterns (e.g.
  // user removed VAT → supplier's prices are tax-inclusive). See
  // ~/.claude/plans/serialized-cuddling-quokka.md
  const [parsedExtracted, setParsedExtracted] = useState(null);
  const [learnRuleModal,  setLearnRuleModal]  = useState(null); // { rule, onConfirm, onDecline }

  useEffect(() => {
    // include_empty=true bypasses the strict "must have a purchase doc" filter
    // applied to /ספקים. Required here because the user uploads the FIRST doc
    // for a brand-new supplier — without this, that supplier never appears in
    // the dropdown and you can't pick it.
    suppliersApi.getAll({ include_empty: true }).then(r => setSuppliers(r.data));
  }, []);

  // Notify if there are open credits when scanning a credit doc for a supplier
  useEffect(() => {
    const isCreditType = ['חשבונית זיכוי', 'תעודת החזרה'].includes(form.doc_type);
    if (isCreditType && form.supplier_id && stage === 'review') {
      creditsApi.getOpenCount(form.supplier_id).then(r => setOpenCredits(r.data)).catch(() => {});
    } else {
      setOpenCredits(null);
    }
  }, [form.doc_type, form.supplier_id, stage]);

  const applyMatch = async (supplierId, rawItems, docType = form.doc_type) => {
    console.log(`[catalog] applyMatch: supplier=${supplierId}, ${rawItems.length} items`);
    if (!supplierId) {
      console.warn('[catalog] applyMatch skipped — no supplierId yet');
      return rawItems;
    }
    try {
      const r = await catalogApi.match(supplierId, rawItems);
      return rawItems.map((it, idx) => {
        const enriched = r.data[idx] || {};
        const hit = !!(enriched.catalog_id);

        // Log AI-extracted values vs catalog-found values
        console.log(
          `[catalog] [${idx}] AI: sku="${it.sku}" desc="${it.description}" price=${it.unit_price}` +
          (hit ? ` | CATALOG HIT: name="${enriched.catalog_name}" sku="${enriched.sku}" price=${enriched.catalog_last_price}` : ' | CATALOG MISS: no match')
        );

        if (!hit) {
          // No catalog match — keep everything AI extracted
          return { ...it };
        }

        // Catalog hit: trust the AI's unit_price (it reads the actual price
        // off the new invoice, which is by definition the latest). The
        // catalog's last_price is kept ONLY for the UI to highlight a price
        // change and surface "היה ₪X עכשיו ₪Y" — it does NOT override.
        const sku = it.sku || enriched.sku || '';
        // Name: use catalog canonical name (overrides AI's noisier extraction)
        const description = enriched.catalog_name || it.description || '';
        const unitPrice = parseFloat(it.unit_price) || 0;
        // Delivery notes that arrive without a price: auto-fill from the product's
        // CURRENT price (latest real price from document history; server computes
        // it sku-or-name and falls back to the catalog price). Marked "משוער"; the
        // server skips writing estimated prices back to the catalog on save.
        const curP = parseFloat(enriched.catalog_current_price) || 0;
        const estimated = docType === 'תעודת משלוח' && unitPrice <= 0 && curP > 0;
        const finalUnit = estimated ? curP : unitPrice;
        // Use shared calcLineTotal so deposit/purchase_tax are included per the
        // active formula. Inline math here previously skipped both and silently
        // ignored supplier-level deposit/mas-kniya flags.
        const itemForCalc = { ...it, unit_price: finalUnit };
        const lineTotal = calcLineTotal(itemForCalc);

        return {
          ...it,
          sku,
          description,
          unit_price: finalUnit,
          line_total: lineTotal,
          price_estimated: estimated ? 1 : 0,
          catalog_last_price:  enriched.catalog_last_price,   // baseline for the price-change badge
          catalog_fixed_price: enriched.catalog_fixed_price,  // user-agreed price; overrides last_price as anchor when > 0
        };
      });
    } catch (e) {
      console.error('[catalog] applyMatch error:', e.message);
      return rawItems;
    }
  };

  const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
  const MAX_FILE_MB = 20;                          // matches the server multer cap
  const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList);
    const typeOk = incoming.filter(f => ALLOWED.includes(f.type));
    if (typeOk.length < incoming.length)
      toast('חלק מהקבצים לא נתמכים. השתמש ב-PDF, JPG או PNG', 'error');
    const valid = typeOk.filter(f => f.size <= MAX_FILE_BYTES);
    if (valid.length < typeOk.length)
      toast(`חלק מהקבצים גדולים מדי — עד ${MAX_FILE_MB}MB לקובץ`, 'error');
    setStagedFiles(prev => {
      const combined = [...prev, ...valid.map(f => ({
        file: f,
        previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
      }))];
      if (combined.length > 3) {
        toast('ניתן להעלות עד 3 קבצים בו-זמנית', 'error');
        combined.slice(3).forEach(sf => sf.previewUrl && URL.revokeObjectURL(sf.previewUrl));
        return combined.slice(0, 3);
      }
      return combined;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFile = (idx) => {
    setStagedFiles(prev => {
      const sf = prev[idx];
      if (sf?.previewUrl) URL.revokeObjectURL(sf.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearStaged = () => {
    setStagedFiles(prev => { prev.forEach(sf => sf.previewUrl && URL.revokeObjectURL(sf.previewUrl)); return []; });
  };

  // Blank the AI-derived line items + header fields before a manual-entry
  // review. Without this, uploading a second document (after "← חזור להעלאה")
  // that skips AI or fails extraction would inherit the PREVIOUS document's
  // rows/totals, which then get saved. Preserves user-set context that isn't
  // AI-derived: supplier_id (may be pre-selected before upload), doc_type,
  // status.
  const resetForManualEntry = () => {
    setItems([]);
    setCutLineCount(null);
    // Same reason as the rows above: without this, a מרכזת typed in manually
    // after an AI-extracted one would be saved carrying the PREVIOUS invoice's
    // delivery-note references, and its coverage report would describe a
    // document the user never opened.
    setCoveredDocs([]);
    setForm(f => ({
      ...f,
      doc_num: '', date: '', subtotal: '', vat: '', total: '',
      invoice_discount: 0, invoice_discount_type: 'percent',
    }));
  };

  // ─── Bulk upload (admin only) ──────────────────────────────────────────────
  // Each staged file becomes its own draft doc. We call the existing
  // /documents/upload endpoint once per file (1 file in formdata) so the AI
  // extracts a single doc per call, then post-save with status='טיוטה'.
  // Supplier matching: try a case-insensitive name match against the loaded
  // suppliers list; if no hit, suppliersApi.create is idempotent and returns
  // the existing row when the name's already there (race-safe).
  const BULK_MAX = 10;
  const BULK_MIN = 2;

  const addBulkFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const typeOk = incoming.filter(f => ALLOWED.includes(f.type));
    if (typeOk.length < incoming.length)
      toast('חלק מהקבצים לא נתמכים. השתמש ב-PDF, JPG או PNG', 'error');
    const valid = typeOk.filter(f => f.size <= MAX_FILE_BYTES);
    if (valid.length < typeOk.length)
      toast(`חלק מהקבצים גדולים מדי — עד ${MAX_FILE_MB}MB לקובץ`, 'error');
    setBulkStaged(prev => {
      const combined = [...prev, ...valid.map(f => ({
        file: f,
        previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
      }))];
      if (combined.length > BULK_MAX) {
        toast(`ניתן להעלות עד ${BULK_MAX} קבצים במצב מרובה`, 'error');
        combined.slice(BULK_MAX).forEach(sf => sf.previewUrl && URL.revokeObjectURL(sf.previewUrl));
        return combined.slice(0, BULK_MAX);
      }
      return combined;
    });
  };

  const removeBulkFile = (idx) => {
    setBulkStaged(prev => {
      const sf = prev[idx];
      if (sf?.previewUrl) URL.revokeObjectURL(sf.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearBulkStaged = () => {
    setBulkStaged(prev => { prev.forEach(sf => sf.previewUrl && URL.revokeObjectURL(sf.previewUrl)); return []; });
  };

  // Resolve a supplier_id for an AI-extracted supplier_name. Match against the
  // already-loaded suppliers list, case- and whitespace-insensitive. We deliberately
  // never auto-create here — supplier creation is admin-only via ניהול ספקים, so
  // an AI-extracted name with no match means the file gets skipped (the bulk
  // summary toast surfaces the count and the user creates the supplier manually).
  // Same rule as the server's upload match and its one-name-per-restaurant
  // check (utils/normName.js): case, spaces and quote variants ignored.
  //
  // The server's own match comes first: it also recognises a supplier by one
  // of its aliases together with the ח.פ. printed on the invoice
  // (lib/supplierAliases.js) — a supplier unified under one platform-wide name
  // no longer matches its printed name. Trusted only when it is in this
  // restaurant's list.
  const resolveSupplierIdByName = (rawName, matchedSupplierId = null) => {
    if (matchedSupplierId != null && suppliers.some(s => s.id === matchedSupplierId)) return matchedSupplierId;
    const key = normName(rawName);
    if (!key) return null;
    const found = suppliers.find(s => normName(s.name) === key);
    return found?.id || null;
  };

  const processBulk = async () => {
    if (bulkStaged.length < BULK_MIN) {
      toast(`יש להעלות לפחות ${BULK_MIN} קבצים במצב מרובה`, 'error');
      return;
    }
    setBulkProgress({ current: 0, total: bulkStaged.length, running: true });
    let saved = 0;
    let failed = 0;
    let partial = 0;
    // Read only up to the AI's output cap (#338): the draft lacks the lines
    // after the cut, and nothing on /drafts says so for a חשבונית מרכזת.
    let cut = 0;
    let duplicates = 0;
    // A second copy of a document the restaurant already has: the server
    // sets it aside at once (auto_deleted, #307) — it never reaches /drafts.
    let setAside = 0;
    const duplicateNames = [];
    for (let i = 0; i < bulkStaged.length; i++) {
      const sf = bulkStaged[i];
      setBulkProgress(p => ({ ...p, current: i + 1 }));
      try {
        const res = await uploadBulkFile(sf.file);
        const { extracted, file_path, file_paths, truncated, recipient_match, recipient_name, recipient_business_id, matched_supplier_id } = res.data;
        const paths = Array.isArray(file_paths) && file_paths.length > 0 ? file_paths : (file_path ? [file_path] : []);
        if (!extracted) {
          // The scan is stored but nothing was read (parse failed, or the AI
          // call failed and the skip-AI retry stored it). Same rule as the
          // folder ingester: keep it as an empty draft to fill by hand rather
          // than drop a file the person believes they uploaded.
          if (paths.length === 0) throw new Error('no file_paths returned');
          const placeholderRes = await documentsApi.save(placeholderDraftBody(paths));
          if (placeholderRes?.data?.auto_deleted) {
            setAside++;
            console.warn(`[bulk] file #${i + 1} "${sf.file.name}" — identical to document #${placeholderRes.data.auto_deleted.duplicate_of}; set aside`);
            continue;
          }
          saved++;
          partial++;
          console.warn(`[bulk] file #${i + 1} "${sf.file.name}" — no extraction; saved as an empty draft for manual entry`);
          continue;
        }
        // No match → save with supplier_id=null. The draft still lands in
        // the queue with a "לא זוהה ספק" warning; the admin assigns a
        // supplier from the doc view before approving. Never block the
        // upload — bulk's whole point is "process now, review later".
        const supplierId = resolveSupplierIdByName(extracted.supplier_name, matched_supplier_id);
        if (!supplierId) {
          console.warn(`[bulk] file #${i + 1} "${sf.file.name}" — AI supplier "${extracted.supplier_name || '(empty)'}" not found; saving draft without supplier for manual assignment.`);
        }
        const saveRes = await documentsApi.save({
          supplier_id: supplierId || null,
          doc_type: extracted.doc_type || 'חשבונית',
          doc_num: extracted.doc_num || '',
          date: extracted.date || '',
          subtotal: extracted.subtotal ?? 0,
          vat: extracted.vat ?? 0,
          total: roundDocTotal(extracted.total ?? 0),
          status: 'טיוטה',
          invoice_discount: extracted.invoice_discount ?? 0,
          invoice_discount_type: extracted.invoice_discount_type || 'percent',
          file_path,
          file_paths: paths,
          items: (extracted.items || []).map(it => ({
            sku: it.sku || '',
            description: it.description || '',
            qty: it.qty || 1,
            unit_price: it.unit_price || 0,
            discount: it.discount || 0,
            discount_type: it.discount_type || 'percent',
            line_total: it.line_total || 0,
            deposit: it.deposit || 0,
            purchase_tax: it.purchase_tax || 0,
          })),
          // Only a חשבונית מרכזת carries these; the server ignores them on
          // every other doc_type, so passing them through unconditionally is
          // safe and keeps the bulk path in step with the single-upload one.
          covered_documents: extracted.covered_documents || [],
          // Bulk mode: server flags duplicates with duplicate_warning=1
          // instead of blocking. Admin resolves on /drafts before promoting.
          _bulk_mode: true,
          source: 'bulk',
          // Persist the AI's raw supplier guess when no match was found so the
          // draft-edit view can pre-fill the "+ הוסף ספק חדש" modal with it.
          extracted_supplier_name: supplierId ? null : (extracted.supplier_name || null),
          // Persist recipient validation result so the draft-edit view can
          // raise the same "doc may belong to another restaurant" banner.
          extracted_recipient_name: recipient_name || null,
          recipient_business_id: recipient_business_id || null,
          recipient_match: recipient_match || null,
        });
        if (saveRes?.data?.auto_deleted) {
          setAside++;
          console.warn(`[bulk] file #${i + 1} "${sf.file.name}" — a copy of document #${saveRes.data.auto_deleted.duplicate_of}; set aside`);
          continue;
        }
        saved++;
        if (truncated) {
          cut++;
          console.warn(`[bulk] file #${i + 1} "${sf.file.name}" — too long; only the first ${extracted.items?.length ?? 0} lines were read`);
        }
        if (saveRes?.data?.duplicate_warning === 1) {
          duplicates++;
          duplicateNames.push(sf.file.name);
          console.warn(`[bulk] file #${i + 1} "${sf.file.name}" — duplicate of existing doc (saved as draft with warning)`);
        }
      } catch (e) {
        console.error(`[bulk] file #${i + 1} "${sf.file.name}" failed:`, e.response?.data?.error || e.message);
        failed++;
      }
    }
    setBulkProgress({ current: 0, total: 0, running: false });
    clearBulkStaged();
    const dupMsg = duplicates > 0
      ? `כולל ${duplicates} כפיל${duplicates > 1 ? 'ים' : ''}: ${duplicateNames.slice(0, 3).join(', ')}${duplicateNames.length > 3 ? '…' : ''}`
      : '';
    // partial is a subset of saved (hence "מתוכן"); failed is not. Duplicates
    // come first because an unread draft has no supplier and can never be
    // flagged as one — the two notes describe different files, so they are
    // joined with a separator rather than spliced together.
    const partialMsg = partial > 0 ? `מתוכן ${partial} למילוי ידני — לא נותחו אוטומטית` : '';
    const cutMsg = cut > 0 ? `${cut} נקרא${cut > 1 ? 'ו' : ''} חלקית — מסמך ארוך, השלם שורות ידנית` : '';
    const notes = [dupMsg.trim(), partialMsg, cutMsg].filter(Boolean).join(' · ');
    // Restorable from the Command Center's מחוקים for 60 days.
    const asideMsg = setAside === 1 ? 'עותק כפול אחד הועבר למחוקים'
      : setAside > 1 ? `${setAside} עותקים כפולים הועברו למחוקים` : '';
    const savedMsg = [`נשמרו ${saved} טיוטות${notes ? ` (${notes})` : ''}`, asideMsg].filter(Boolean).join(' · ');
    track(EVENTS.BULK_UPLOAD_COMPLETED, { saved, failed, duplicates, partial, cut, set_aside: setAside });
    if (saved > 0 && failed === 0 && duplicates === 0 && partial === 0 && cut === 0) toast(savedMsg);
    else if (saved > 0 && failed === 0) toast(`${savedMsg} — בדוק ב-/drafts`, 'error');
    else if (saved > 0 && failed > 0) toast(`${savedMsg}, ${failed} נכשלו — ראה קונסולה`, 'error');
    else if (setAside > 0 && failed === 0) toast(`כל הקבצים כבר קיימים · ${asideMsg}`);
    else if (setAside > 0) toast(`${asideMsg}, ${failed} נכשלו — ראה קונסולה`, 'error');
    else toast('לא נשמרה אף טיוטה — ראה קונסולה', 'error');
    if (saved > 0) navigate('/drafts');
  };

  // A failure the next call may survive: the server's 500 on an AI error
  // (which is also how it reports a file Claude rejected), Claude overloaded
  // (529), or no response at all. A 4xx (too large, too many files, auth) or
  // the storage 502 is final and is not retried. Same rule as the folder
  // ingester (tools/ingester/src/processFile.js), which additionally retries
  // the AI once more before falling back — nobody is waiting on it.
  const isTransientUploadError = (e) => {
    const status = e?.response?.status;
    return status === undefined || status === 500 || status === 529;
  };

  // Upload one bulk file: AI first; on a transient failure, once more with
  // skip_ai so the scan is at least stored. A final failure throws.
  // The one shape every bulk upload request has; the retry differs only by skip_ai.
  const bulkForm = (file, { skipAi = false } = {}) => {
    const fd = new FormData();
    fd.append('files', file);
    if (skipAi) fd.append('skip_ai', 'true');
    fd.append('source', 'bulk'); // names the upload path on the server's extraction record
    return fd;
  };

  const uploadBulkFile = async (file) => {
    try {
      return await documentsApi.upload(bulkForm(file));
    } catch (e) {
      if (!isTransientUploadError(e)) throw e;
      console.warn(`[bulk] "${file.name}" — AI upload failed (${e.response?.data?.error || e.message}); storing without AI`);
      return await documentsApi.upload(bulkForm(file, { skipAi: true }));
    }
  };

  // The draft a stored-but-unread scan becomes: type אחר, no supplier, no
  // lines — the same shape the ingester saves, so the queue shows it with the
  // "לא זוהה ספק" warning and a person fills it in from the review screen.
  const placeholderDraftBody = (paths) => ({
    supplier_id: null,
    doc_type: 'אחר',
    doc_num: '',
    date: '',
    subtotal: 0,
    vat: 0,
    total: 0,
    status: 'טיוטה',
    invoice_discount: 0,
    invoice_discount_type: 'percent',
    file_path: paths[0],
    file_paths: paths,
    items: [],
    _bulk_mode: true,
    source: 'bulk',
  });

  const processFiles = useCallback(async () => {
    if (stagedFiles.length === 0) return;
    setProcessing(true);
    // A run that follows a prior failure is a recovery attempt.
    if (extractFailedRef.current) {
      track(EVENTS.EXTRACT_RETRIED, {
        prior_outcome: extractFailedRef.current,
        changed_ai_toggle: aiToggleAtFailureRef.current !== useAI,
        file_count: stagedFiles.length,
      });
      extractFailedRef.current = null;
    }
    if (!useAI) track(EVENTS.AI_EXTRACTION_SKIPPED, { entry_mode: 'toggle_off', file_count: stagedFiles.length });
    // Clear any prior document's AI baseline + edit tracking up front, so a
    // skipped/partial (manual-entry) run that returns early can't later diff
    // against a stale parsedExtracted and misattribute corrections. The success
    // path re-seeds both below once the new extraction lands.
    setParsedExtracted(null);
    resetCorrectionTracking(0);
    try {
      const fd = new FormData();
      stagedFiles.forEach(sf => fd.append('files', sf.file));
      if (!useAI) fd.append('skip_ai', 'true');
      fd.append('source', 'single'); // names the upload path on the server's extraction record
      const res = await documentsApi.upload(fd);
      const { extracted, file_path, file_paths, partial, truncated, rawText, skipped, recipient_match, recipient_name, recipient_business_id, recipient_decided_by, restaurant_name, restaurant_business_id, matched_supplier_id, supplier_business_id } = res.data;
      setPrintedSupplierBid(supplier_business_id || '');
      // Capture recipient validation so the review screen can banner a mismatch.
      setRecipientCheck({
        match: recipient_match || null,
        recipient_name: recipient_name || '',
        recipient_business_id: recipient_business_id || null,
        decided_by: recipient_decided_by || 'name',
        restaurant_name: restaurant_name || '',
        restaurant_business_id: restaurant_business_id || null,
      });
      if (recipient_match === 'mismatch' || recipient_match === 'not_found') {
        track(EVENTS.RECIPIENT_MISMATCH_SHOWN, { match_type: recipient_match, mode: 'single' });
      }
      console.log('[upload-response] file_path=', file_path, 'file_paths=', file_paths);
      setFilePath(file_path);
      setFilePaths(Array.isArray(file_paths) && file_paths.length > 0 ? file_paths : (file_path ? [file_path] : []));
      // Fresh object URLs (independent of the thumbnails clearStaged revokes)
      // so the review pane can display the pages that were just uploaded.
      setReviewPreviews(prev => {
        prev.forEach(p => URL.revokeObjectURL(p.url));
        return stagedFiles.map(sf => ({
          url: URL.createObjectURL(sf.file),
          isPdf: sf.file.type === 'application/pdf',
        }));
      });

      // User chose to skip AI — go straight to manual review
      if (skipped) {
        resetForManualEntry();
        clearStaged();
        setStage('review');
        toast('הקובץ הועלה — אנא מלא את הפרטים ידנית');
        return;
      }

      if (partial || !extracted) {
        // Claude couldn't return valid JSON — switch to manual entry with raw hint
        resetForManualEntry();
        setOcrRawText(rawText || '');
        clearStaged();
        setStage('review');
        toast('לא ניתן לנתח את המסמך אוטומטית — אנא מלא את הפרטים ידנית', 'error');
        return;
      }

      // Resolve supplier: form pre-selection wins; otherwise match AI name
      // case-insensitively against the existing supplier list. Substring /
      // fuzzy matching was removed because it produced false positives
      // ("אבי הקצב" silently matched "אבי" etc). Never auto-create — supplier
      // creation is admin-only via ניהול ספקים, so a no-match leaves the
      // dropdown empty for the user to pick (or to create the supplier
      // elsewhere and retry).
      let supplierId = form.supplier_id;
      // Clear any leftover unmatched-name suggestion from a PREVIOUS upload
      // (page-level state survives "← חזור להעלאה"). Only the no-match branch
      // below re-populates it, so a matched / pre-selected / name-less
      // extraction never inherits the prior document's supplier suggestion.
      setNewSupplierName('');
      if (!supplierId && extracted.supplier_name) {
        const matchedId = resolveSupplierIdByName(extracted.supplier_name, matched_supplier_id);
        const matched = matchedId ? suppliers.find(s => s.id === matchedId) : null;
        supplierId = matched?.id ? String(matched.id) : '';
        console.log(
          `[upload] AI supplier="${extracted.supplier_name}" -> ` +
          (matched ? `matched "${matched.name}" id=${matched.id}` : 'NO MATCH — user must pick or create via ניהול ספקים')
        );
        // No matching supplier — the dropdown is left empty so the user can
        // either pick another existing supplier or click "+ הוסף ספק חדש"
        // next to the dropdown to create one with the AI-extracted name
        // pre-filled. We stash the extracted name on a state field for the
        // inline-create modal to read.
        if (!matched) {
          setNewSupplierName(extracted.supplier_name.trim());
          track(EVENTS.SUPPLIER_UNMATCHED, { context: 'single_upload' });
        }
      } else {
        console.log(`[upload] Using pre-selected supplier_id="${supplierId}"`);
      }

      // Intentionally NOT taking extracted.subtotal/vat/total — the AI typically
      // reads "חייב במע"מ" off the invoice, which is the post-discount taxable
      // amount, not the pre-discount items sum. The useEffect below re-derives
      // subtotal = SUM(items.line_total) and chains vat/total off it, matching
      // DocumentViewPage's edit-mode logic.
      setForm(prev => ({
        ...prev,
        doc_type: extracted.doc_type || prev.doc_type,
        doc_num: extracted.doc_num || '',
        date: extracted.date || '',
        supplier_id: supplierId || prev.supplier_id,
        invoice_discount: extracted.invoice_discount ?? 0,
        invoice_discount_type: extracted.invoice_discount_type || 'percent',
      }));

      // When the supplier has no deposit / purchase_tax flag, force the
      // corresponding item fields to 0 — the AI sometimes fills them anyway
      // (e.g. a line description containing "פיקדון" triggers a value). Without
      // this, calcLineTotal silently double-counts.
      const matchedSupplier = suppliers.find(s => String(s.id) === String(supplierId));
      const supHasDeposit     = !!matchedSupplier?.has_deposit;
      const supHasPurchaseTax = !!matchedSupplier?.has_purchase_tax;

      setCoveredDocs(Array.isArray(extracted.covered_documents) ? extracted.covered_documents : []);

      const rawItems = (extracted.items || []).map((it, i) => {
        const merged = {
          discount: 0, discount_type: 'percent', credit_qty: 0, credit_price_diff: 0, credit_total: 0,
          deposit: 0, purchase_tax: 0,
          ...it, _key: i,
        };
        // Only strip AI-extracted deposit/tax when we POSITIVELY know the
        // supplier doesn't have the flag. If supplier name didn't match (no
        // matchedSupplier), preserve the AI values — handleSupplierChange will
        // sanitize once the user picks a supplier. Without this, a name
        // mismatch (e.g. AI "להפצה" vs DB "להפצת") silently zeroed deposit.
        if (matchedSupplier && !supHasDeposit)     merged.deposit      = 0;
        if (matchedSupplier && !supHasPurchaseTax) merged.purchase_tax = 0;
        merged.line_total   = calcLineTotal(merged);
        merged.credit_total = calcCreditTotal(merged);
        return merged;
      });
      console.log(`[upload] ${rawItems.length} items extracted. SKUs: ${rawItems.map(it=>it.sku||'—').join(', ')}`);
      setItems(supplierId ? await applyMatch(supplierId, rawItems, extracted.doc_type || form.doc_type) : rawItems);
      resetCorrectionTracking(rawItems.length);
      // Snapshot Claude's raw read so we can diff it against the user's edits
      // at save time and surface a "ללמוד כלל?" suggestion.
      setParsedExtracted({
        supplier_name: extracted.supplier_name || '',
        subtotal: extracted.subtotal ?? 0,
        vat: extracted.vat ?? 0,
        total: extracted.total ?? 0,
        invoice_discount: extracted.invoice_discount ?? 0,
        invoice_discount_type: extracted.invoice_discount_type || 'percent',
      });
      setOcrRawText('');
      setCutLineCount(truncated ? rawItems.length : null);
      clearStaged();
      setStage('review');
      track(EVENTS.DOCUMENT_EXTRACTED, { file_count: stagedFiles.length, used_ai: useAI, partial: !!partial, truncated: !!truncated });
      if (truncated) toast(`המסמך ארוך מדי — נקראו ${rawItems.length} שורות ראשונות בלבד`, 'error');
      else toast(`המסמך עובד בהצלחה${stagedFiles.length > 1 ? ` (${stagedFiles.length} קבצים)` : ''}`);
    } catch (e) {
      const serverMsg = e.response?.data?.error;
      const isTimeout = e.code === 'ECONNABORTED' || e.message?.includes('timeout');
      const msg = serverMsg
        || (isTimeout ? 'העיבוד לקח יותר מדי זמן — נסה שוב עם פחות קבצים או תמונות קטנות יותר' : 'שגיאה בעיבוד המסמך — נסה שוב');
      track(EVENTS.DOCUMENT_EXTRACT_FAILED, { file_count: stagedFiles.length, used_ai: useAI, timeout: isTimeout });
      extractFailedRef.current = isTimeout ? 'timeout' : 'failed';
      aiToggleAtFailureRef.current = useAI;
      toast(msg, 'error');
    } finally {
      setProcessing(false);
    }
  }, [stagedFiles, suppliers, form.supplier_id, useAI]); // eslint-disable-line react-hooks/exhaustive-deps
  // useAI MUST be a dep: the toggle only renders after a file is staged, so a
  // user turning off "חלץ נתונים אוטומטית" does so AFTER stagedFiles last
  // changed. Without useAI here, processFiles keeps the stale useAI=true from
  // stage-time and never appends skip_ai — the toggle silently does nothing and
  // heavy docs still hit the AI (and time out). See the מ.ר כלים 225-item invoices.

  const handleSupplierChange = async (supplierId) => {
    setForm(prev => ({ ...prev, supplier_id: supplierId }));
    if (supplierId && items.length > 0) {
      const matched = await applyMatch(supplierId, items);
      // Strip deposit / purchase_tax from items when the new supplier doesn't
      // have the flag (e.g. switching from a wine supplier to a non-deposit
      // supplier should drop the bottle-deposit values, not double-count them).
      const sup = suppliers.find(s => String(s.id) === String(supplierId));
      const supHasDeposit     = !!sup?.has_deposit;
      const supHasPurchaseTax = !!sup?.has_purchase_tax;
      const sanitized = matched.map(it => {
        const next = { ...it };
        if (!supHasDeposit)     next.deposit      = 0;
        if (!supHasPurchaseTax) next.purchase_tax = 0;
        next.line_total   = calcLineTotal(next);
        next.credit_total = calcCreditTotal(next);
        return next;
      });
      setItems(sanitized);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const updateItem = (idx, field, value) => {
    editedItemFieldsRef.current.add(field);
    editedItemRowsRef.current.add(idx);
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      // Typing a real price clears the "estimated" mark — it is now a real price.
      if (field === 'unit_price') updated.price_estimated = 0;
      if (['qty', 'unit_price', 'discount', 'discount_type', 'deposit', 'purchase_tax'].includes(field)) {
        updated.line_total   = calcLineTotal(updated);
        updated.credit_total = calcCreditTotal(updated);
      }
      if (field === 'credit_qty' || field === 'credit_price_diff') {
        updated.credit_total = calcCreditTotal(updated);
      }
      return updated;
    }));
  };

  // Auto-change status when credit_qty is entered/cleared.
  //
  // Twin of the effect in DocumentViewPage — read the reasoning there. The
  // clear branch only undoes a 'לא תקין' this review session set, so a flag
  // raised elsewhere (the link modal's unreconciled-מרכזת case, which involves
  // no credit lines) survives an edit.
  const autoInvalidRef = useRef(false);
  useEffect(() => {
    if (stage !== 'review') { autoInvalidRef.current = false; return; }
    const hasCredits = items.some(it =>
      (parseFloat(it.credit_qty) || 0) > 0 || (parseFloat(it.credit_price_diff) || 0) > 0
    );
    setForm(f => {
      if (hasCredits && f.status !== 'לא תקין') {
        autoInvalidRef.current = true;
        return { ...f, status: 'לא תקין' };
      }
      if (!hasCredits && f.status === 'לא תקין' && autoInvalidRef.current) {
        autoInvalidRef.current = false;
        return { ...f, status: 'תקין' };
      }
      return f;
    });
  }, [items, stage]);

  // Supplier-rule probe — derived from current state, not an effect, so the
  // auto-recalc below sees it on the same render (no race). Two patterns today:
  //   • VAT-inclusive  — force vat=0 (prices already include VAT)
  //   • Partial VAT    — force vat = Claude's extracted value (variable VAT
  //                      driven by item mix, typical for produce suppliers)
  // Both flip vatMode to manual so the toggle visually reflects the math.
  // See: ~/.claude/plans/serialized-cuddling-quokka.md
  // VAT-rule detection lives in the shared detectVatRule util so this review
  // screen and the document edit screen (DocumentViewPage) stay in lockstep.
  const ruleSupplier = suppliers.find(s => String(s.id) === String(form.supplier_id));
  const { vatInclusive: supplierVatInclusive, partialVat: supplierPartialVat } = detectVatRule(ruleSupplier?.invoice_rules, form.doc_type);
  useEffect(() => {
    if (stage !== 'review') return;
    if ((supplierVatInclusive || supplierPartialVat) && vatMode !== 'manual') {
      console.log(`[rule] supplier ${ruleSupplier?.id} ${supplierVatInclusive ? 'VAT-inclusive' : 'partial-VAT'} — flipping toggle to manual`);
      setVatMode('manual');
    }
  }, [supplierVatInclusive, supplierPartialVat, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Partial-VAT seeding — runs ONCE when the partial-VAT rule activates with a
  // parsedExtracted available. Copies Claude's extracted vat into the form so
  // the auto-recalc below (which treats partial-VAT as manual mode) preserves
  // it. Without this seed, the form starts with vat='' and the manual path
  // sees parseFloat('')||0=0 — the user has to re-type it every upload.
  useEffect(() => {
    if (stage !== 'review' || !supplierPartialVat || !parsedExtracted) return;
    const seedVat = Number(parsedExtracted.vat) || 0;
    console.log(`[rule] seeding partial-VAT value from Claude: ${seedVat}`);
    setForm(f => ({ ...f, vat: seedVat }));
  }, [supplierPartialVat, parsedExtracted, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-recalc subtotal (always) + vat (auto mode unless rule-suppressed) +
  // total whenever the inputs change. subtotal MUST equal SUM(items.line_total)
  // — the AI's extracted subtotal is ignored on purpose (see processFiles for
  // why). Mirrors the edit-mode useEffect in DocumentViewPage so both flows
  // agree on the math.
  useEffect(() => {
    if (stage !== 'review') return;
    const subtotal = round2(items.reduce((s, it) => s + (parseFloat(it.line_total) || 0), 0));
    setForm(f => {
      const taxable = subtotal - calcDiscountAmount(subtotal, f.invoice_discount, f.invoice_discount_type);
      let vat;
      if (supplierVatInclusive) {
        vat = 0;
      } else if (supplierPartialVat) {
        // Partial VAT: behave like manual mode — preserve whatever's in the
        // form (seeded above from Claude's extraction; the user can override).
        vat = parseFloat(f.vat) || 0;
      } else if (vatMode === 'auto') {
        vat = round2(taxable * (vatRate / 100));
      } else {
        vat = parseFloat(f.vat) || 0;
      }
      return { ...f, subtotal, vat, total: roundDocTotal(taxable + round2(vat)) };
    });
  }, [items, stage, vatMode, vatRate, form.invoice_discount, form.invoice_discount_type, supplierVatInclusive, supplierPartialVat]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => { itemsAddedRef.current += 1; setItems(prev => [...prev, { sku: '', description: '', qty: 1, unit_price: 0, discount: 0, discount_type: 'percent', line_total: 0, credit_qty: 0, credit_price_diff: 0, credit_total: 0, deposit: 0, purchase_tax: 0, _key: Date.now() }]); };
  const removeItem = (idx) => { itemsRemovedRef.current += 1; setItems(prev => prev.filter((_, i) => i !== idx)); };

  // VAT is computed on taxable = subtotal − invoice-level discount.
  const calcDiscountAmount = (subtotal, discount, discountType) => {
    const s = parseFloat(subtotal) || 0;
    const d = parseFloat(discount) || 0;
    return discountType === 'amount' ? d : s * (d / 100);
  };

  const recalcTotals = () => {
    // Recompute every row's line_total + credit_total from the formula before
    // summing — handles drafts loaded with AI-extracted line_totals that don't
    // match (net + deposit + tax) × qty.
    const recomputed = items.map(it => ({
      ...it,
      line_total:   calcLineTotal(it),
      credit_total: calcCreditTotal(it),
    }));
    setItems(recomputed);
    const subtotal = recomputed.reduce((s, it) => s + (parseFloat(it.line_total) || 0), 0);
    const taxable = subtotal - calcDiscountAmount(subtotal, form.invoice_discount, form.invoice_discount_type);
    const vat = vatMode === 'auto'
      ? round2(taxable * (vatRate / 100))
      : parseFloat(form.vat) || 0;
    const total = roundDocTotal(taxable + round2(vat));
    setForm(prev => ({ ...prev, subtotal, vat, total }));
  };

  // When manual VAT is typed, keep total in sync
  const handleManualVat = (value) => {
    const vat = parseFloat(value) || 0;
    const subtotal = parseFloat(form.subtotal) || 0;
    const taxable = subtotal - calcDiscountAmount(subtotal, form.invoice_discount, form.invoice_discount_type);
    setForm(prev => ({ ...prev, vat: value, total: roundDocTotal(taxable + round2(vat)) }));
  };

  // Invoice-level discount edits: accepts a partial patch of
  // { invoice_discount, invoice_discount_type } and re-derives vat + total.
  const handleInvoiceDiscount = (patch) => {
    setForm(prev => {
      const next = { ...prev, ...patch };
      const subtotal = parseFloat(next.subtotal) || 0;
      const taxable = subtotal - calcDiscountAmount(subtotal, next.invoice_discount, next.invoice_discount_type);
      const vat = vatMode === 'auto'
        ? round2(taxable * (vatRate / 100))
        : parseFloat(prev.vat) || 0;
      return { ...next, vat, total: roundDocTotal(taxable + round2(vat)) };
    });
  };

  // rowMismatched now lives in utils/reviewIssues.js (imported above) — shared
  // with DocumentViewPage edit mode and the consolidated issue strip so all
  // three read the exact same predicate.

  const selectedSupplierMethod = () => suppliers.find(s => String(s.id) === String(form.supplier_id))?.payment_method || '';
  // A הוראת קבע supplier's invoice is paid by the server the moment it is
  // saved (tryAutoPay on POST /, same as on approve), so offering "שולם" here
  // would only race that payment and lose the note — same rule, same reason,
  // as DocumentViewPage's copy of this function.
  const autoPaysOnApproval = () =>
    String(selectedSupplierMethod() || '').trim().replace(/['׳]/g, '') === 'הוראת קבע' &&
    ['חשבונית', 'חשבונית מרכזת', 'חשבונית/קבלה', 'חשבון עסקה'].includes(form.doc_type);

  // The payment recorded when "שולם" is ticked. Runs after the save already
  // landed (the document must exist to be paid) and never throws — the
  // caller's flow (rule-learning modal, link/credit offers, navigation)
  // continues either way, matching DocumentViewPage's recordPaymentOnApprove.
  const recordPaymentOnSave = async (savedId, note) => {
    if (!payOnApprove.checked || !form.supplier_id) return;
    try {
      const r = await paymentsApi.create({
        supplier_id: Number(form.supplier_id),
        document_id: Number(savedId),
        payment_method: payOnApprove.payment_method || selectedSupplierMethod() || PAYMENT_METHODS[0],
        payment_date: payOnApprove.payment_date || todayISO(),
        notes: (note || '').trim(),
      });
      track(EVENTS.PAYMENT_RECORDED, { scope: 'single_doc', source: 'upload_review', payment_method: payOnApprove.payment_method, doc_count: 1, month_approved: Boolean(r?.data?.month_approved) });
      toast(r?.data?.month_approved ? 'התשלום נרשם · החודש אושר לתשלום' : 'התשלום נרשם');
    } catch (err) {
      toast(err.response?.data?.error || 'המסמך נשמר, אך רישום התשלום נכשל', 'error');
    }
  };

  // `e` is optional: the payNoteModal's confirm button re-enters this same
  // function directly (not through a form submit), once payNote is filled.
  const handleSave = async (e) => {
    e?.preventDefault();
    // Covers the double-click, the Enter-then-click, and the Ctrl+S shortcut
    // (saveShortcutRef points at this same closure).
    if (savingRef.current) return;
    // Re-derived from doc_type/supplier every call, NOT just payOnApprove.checked:
    // the checkbox's own visibility already depends on isPayableDocType/
    // autoPaysOnApproval, and doc_type can change AFTER the box was ticked
    // without unticking it (nothing resets `checked` on its own). Reading
    // `checked` alone below would then still approve-and-pay a document whose
    // CURRENT type was never eligible for this control.
    const payOnApproveActive = payOnApprove.checked && form.supplier_id
      && isPayableDocType(form.doc_type) && !autoPaysOnApproval();
    // שולם active and no note yet: ask for it first (same as DocumentViewPage's
    // approveCurrent), then come back here via the modal's confirm button,
    // which fills payNote and re-enters.
    if (payOnApproveActive && !payNote.trim()) {
      setPayNoteModal(true);
      return;
    }
    if (!form.supplier_id) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'no_supplier', context: 'upload_review', doc_type: form.doc_type }); toast('נא לבחור ספק', 'error'); return; }
    if (isImplausibleDateYear(form.date)) { track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'implausible_date', context: 'upload_review', doc_type: form.doc_type }); toast('השנה בתאריך חריגה — תקני את התאריך לפני שמירה', 'error'); return; }
    const bad = items.map((it, i) => ({ i, it })).filter(({ it }) => rowMismatched(it));
    if (bad.length > 0) {
      track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'row_total_mismatch', context: 'upload_review', doc_type: form.doc_type, bad_row_count: bad.length });
      const first = bad[0];
      const expected = calcLineTotal(first.it);
      const actual   = parseFloat(first.it.line_total) || 0;
      toast(
        `שורה ${first.i + 1}: סך הכל (${formatCurrency(actual)}) לא תואם כמות × מחיר נטו (${formatCurrency(expected)}). תקני לפני שמירה.`,
        'error'
      );
      return;
    }
    // Past validation — from here on a request may hit the server, so lock the
    // button. Released only on failure: a successful save always ends in a
    // navigation or a modal that navigates, and re-enabling in between would
    // let a second click create a duplicate of the document just saved.
    savingRef.current = true;
    setSaving(true);
    const releaseSave = () => { savingRef.current = false; setSaving(false); };
    // Flipped the moment the POST resolves. Distinguishes "the save failed"
    // from "the save worked and something afterwards broke" — those need
    // opposite messages, and only the first may re-enable the button.
    let saveLanded = false;

    // Duplicate doc_num pre-check. The server is the source of truth (it
    // returns 409 on conflict and the partial unique index enforces it at the
    // DB level), but a client-side check produces a friendlier error message
    // before sending the full payload. A network hiccup is non-fatal — the
    // server-side guard still catches the conflict.
    // A כרטסת is exempt: its number is the supplier's account number and
    // repeats on every monthly statement (see utils/docTypes.js).
    const trimmedNum = (form.doc_num || '').trim();
    if (trimmedNum && !isDupExemptDocType(form.doc_type)) {
      try {
        const r = await documentsApi.checkDuplicate(form.supplier_id, trimmedNum, form.doc_type);
        if (r.data?.exists) {
          track(EVENTS.DOCUMENT_SAVE_BLOCKED, { reason: 'duplicate_doc_num', context: 'upload_review', doc_type: form.doc_type });
          toast(`מסמך מספר ${trimmedNum} כבר קיים עבור ספק זה — לא ניתן לשמור כפיל`, 'error');
          releaseSave();
          return;
        }
      } catch (e) {
        console.warn('[duplicate-check] skipped due to error:', e.message);
      }
    }
    try {
      console.log('[save-call] sending file_path=', filePath, 'file_paths=', filePaths);
      // שולם active → save directly as תקין (approved), same end state as
      // "אשר והבא" on a draft — POST / runs auto-pay/Dokka-forward off the
      // SAME status it's given, so this is one atomic write rather than a
      // create-as-draft-then-approve round trip. A line with a credit makes
      // that תקין a לא תקין (#260 — the server enforces the same rule).
      const hasCreditLine = items.some(it => (parseFloat(it.credit_qty) || 0) > 0 || (parseFloat(it.credit_price_diff) || 0) > 0);
      const wanted = payOnApproveActive ? 'תקין' : form.status;
      const saveStatus = hasCreditLine && wanted === 'תקין' ? 'לא תקין' : wanted;
      // source + review: which path this came from and, when the save is a
      // decision (non-draft), how long the review stage was open. The server
      // ignores review on a draft save (server/lib/reviewLog.js).
      const r = await documentsApi.save({
        ...form, status: saveStatus, file_path: filePath, file_paths: filePaths, items, covered_documents: coveredDocs,
        source: 'single',
        review: buildReview({ openedAt: reviewOpenedAtRef.current, entry: 'upload_review' }),
      });
      // The write landed. Everything below (analytics, rule detection, the
      // follow-up modals) is post-save work — if any of it throws, the catch
      // must NOT tell the user the document wasn't saved.
      saveLanded = true;
      track(EVENTS.DOCUMENT_SAVED, { doc_type: form.doc_type, line_count: items.length });
      // Flagship: how much did the human correct Claude's parse? Field NAMES and
      // counts only — never values. Fires only when AI actually ran (baseline exists).
      if (parsedExtracted) {
        const near = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.005;
        const headerFieldsChanged = ['subtotal', 'vat', 'total', 'invoice_discount']
          .filter(f => !near(form[f], parsedExtracted[f]));
        const vatZeroed = (Number(parsedExtracted.vat) || 0) > 0 && (Number(form.vat) || 0) === 0;
        const itemFieldsEdited = [...editedItemFieldsRef.current];
        const anyCorrection = headerFieldsChanged.length > 0
          || itemFieldsEdited.length > 0
          || itemsAddedRef.current > 0
          || itemsRemovedRef.current > 0;
        track(EVENTS.AI_EXTRACTION_CORRECTED, {
          doc_type: form.doc_type,
          any_correction: anyCorrection,
          header_fields_changed: headerFieldsChanged,
          header_change_count: headerFieldsChanged.length,
          item_fields_edited: itemFieldsEdited,
          rows_edited_count: editedItemRowsRef.current.size,
          items_added: itemsAddedRef.current,
          items_removed: itemsRemovedRef.current,
          item_count_extracted: extractedItemCountRef.current,
          item_count_final: items.length,
          vat_mode: vatMode,
          vat_zeroed: vatZeroed,
        });
      }
      toast('מסמך נשמר בהצלחה');

      // Auto-pay only surfaces when it actually fires (הוראת קבע supplier on
      // an eligible doc). For every other payment method — מזומן / העברה /
      // אשראי / no method at all — auto-pay legitimately doesn't fire and a
      // toast about it is noise, not information.
      const ap = r?.data?.auto_pay;
      if (ap?.fired) {
        // The standing order already paid it inside the save; a second POST
        // would 400 on "nothing left to pay" and drop the note.
        toast('תשלום אוטומטי בוצע (הוראת קבע)');
      }
      const savedId = r?.data?.id;
      if (payOnApproveActive && !ap?.fired) {
        await recordPaymentOnSave(savedId, payNote);
      }
      const sup = suppliers.find(s => String(s.id) === String(form.supplier_id));
      const goNext = () => {
        if (form.supplier_id) navigate(`/supplier/${form.supplier_id}`);
        else navigate('/');
      };

      // Rule-learning probe: if the user's saved totals diverge from Claude's
      // original parse in a recognized way (currently: VAT removed), suggest
      // saving it as a supplier rule before continuing. The modal calls the
      // existing post-save flow via its onConfirm/onDecline callbacks so we
      // don't bypass the link-delivery / open-credits / navigate logic below.
      let maybeRule = null;
      if (parsedExtracted && form.supplier_id) {
        try {
          const dr = await documentsApi.detectRule({
            parsed: parsedExtracted,
            final: {
              subtotal: parseFloat(form.subtotal) || 0,
              vat:      parseFloat(form.vat) || 0,
              total:    parseFloat(form.total) || 0,
              invoice_discount: parseFloat(form.invoice_discount) || 0,
              invoice_discount_type: form.invoice_discount_type || 'percent',
            },
            supplier_id: form.supplier_id,
            current_rules: sup?.invoice_rules || '',
          });
          maybeRule = dr?.data?.rule || null;
        } catch (e) {
          console.warn('[detect-rule] skipped due to error:', e.message);
        }
      }

      // Continuation: runs the existing link-delivery / open-credits / navigate
      // chain. Extracted into a named lambda so the rule-learning modal can
      // defer it until the user confirms/declines.
      const continueAfterSave = async () => {
        // For חשבונית מרכזת: hold the page and open the link-delivery-notes
        // modal before navigating away.
        if (form.doc_type === 'חשבונית מרכזת' && savedId) {
          setLinkModal({
            invoiceId: savedId,
            invoiceDocNum: form.doc_num,
            invoiceTotal: parseFloat(form.total) || 0,
            // MUST be passed: the modal reconciles NET (reconcileNet compares the
            // selected notes' subtotals against this). Omitted, it read as 0, so
            // this path — the only one that offered linking automatically —
            // showed an amber "הפרש נטו" mismatch on EVERY invoice, even a
            // perfect one, and the confirm button read "שמור בכל זאת". A warning
            // that is always wrong is a warning people learn to click past.
            invoiceSubtotal: parseFloat(form.subtotal) || 0,
            supplierName: sup?.name || '',
            // The status the document was SAVED with — שולם saves straight as
            // תקין. Gates the "mark as לא תקין" offer (a draft is offered it
            // too) and whether the customer can already see the invoice; the
            // modal re-reads the live status from the server as well.
            invoiceStatus: saveStatus,
            // A just-saved invoice has no links yet, so the modal may
            // auto-select the previous month's notes.
            existingLinkCount: 0,
            initialRange: rangeForInvoice(convertRange, form.supplier_id, form.date),
            afterClose: goNext,
          });
          return;
        }
        if (CREDIT_DOC_TYPES.includes(form.doc_type) && savedId && form.supplier_id) {
          try {
            const oc = await creditsApi.getOpenCount(form.supplier_id);
            if ((oc.data?.cnt || 0) > 0) {
              setOpenCreditsModal({
                creditDocId: savedId,
                creditDocTotal: parseFloat(form.total) || 0,
                creditDocSubtotal: parseFloat(form.subtotal) || 0,
                creditDocVat: parseFloat(form.vat) || 0,
                supplierId: form.supplier_id,
                supplierName: sup?.name || '',
                afterClose: goNext,
              });
              return;
            }
          } catch { /* fall through and navigate */ }
        }
        goNext();
      };

      if (maybeRule) {
        const supplierId = form.supplier_id;
        const existing = sup?.invoice_rules || '';
        const appended = existing.trim()
          ? `${existing.trim()}\n${maybeRule.text}`
          : maybeRule.text;
        setLearnRuleModal({
          rule: maybeRule,
          onConfirm: async () => {
            try {
              await suppliersApi.updateInvoiceRules(supplierId, appended);
              toast('הכלל נשמר לספק');
            } catch {
              toast('שגיאה בשמירת הכלל', 'error');
            }
            setLearnRuleModal(null);
            continueAfterSave();
          },
          onDecline: () => {
            setLearnRuleModal(null);
            continueAfterSave();
          },
        });
        return;
      }

      await continueAfterSave();
    } catch (err) {
      // The document IS saved; a post-save step (analytics, rule detection, a
      // follow-up fetch) threw. Saying "לא נשמר" here would be false, and
      // re-enabling the button would invite a duplicate — so do neither. Move
      // the user on, since the work they came to do is done.
      if (saveLanded) {
        console.error('[save] post-save step failed (document IS saved):', err);
        toast('המסמך נשמר, אך אירעה תקלה בשלב שאחריו — אין צורך לשמור שוב.', 'error');
        if (form.supplier_id) navigate(`/supplier/${form.supplier_id}`); else navigate('/');
        return;
      }

      // The document was NOT saved on any path below, so re-enable the button —
      // the user's next action is to retry or fix something.
      releaseSave();

      // 409 → duplicate; the server's localized message is friendlier than
      // a generic "save error".
      if (err?.response?.status === 409 && err.response.data?.duplicate) {
        toast(err.response.data.error || 'מסמך כפיל — השמירה נחסמה', 'error');
        return;
      }

      // No response at all = the request never reached the server. Say so
      // explicitly, including that nothing was saved — otherwise the user has
      // no way to know whether to re-enter the document.
      if (!err?.response) {
        toast('אין חיבור לשרת — המסמך לא נשמר. בדוק את החיבור ונסה שוב.', 'error');
        return;
      }

      // Everything else: the server classifies the failure (see
      // server/lib/saveErrors.js) and returns a Hebrew message that names the
      // cause, says the document was not saved, and gives the next step —
      // including an error code for support. Prefer it over any local guess.
      const data = err.response.data || {};
      toast(data.error || 'שגיאה בשמירה — המסמך לא נשמר. נסה שוב.', 'error');
    }
  };

  // Derive deposit / purchase-tax column visibility from the currently selected
  // supplier's flags. Derived (not stored) so the table re-renders the moment
  // the user switches supplier or creates a new one.
  const selectedSupplier = suppliers.find(s => String(s.id) === String(form.supplier_id));
  const hasDeposit     = !!selectedSupplier?.has_deposit;
  const hasPurchaseTax = !!selectedSupplier?.has_purchase_tax;
  // Memoized so the supplier combobox's filter memo isn't defeated by a fresh
  // array identity on every keystroke re-render.
  const supplierOptions = useMemo(
    () => suppliers.map(s => ({ value: String(s.id), label: s.name })),
    [suppliers],
  );
  const showNewSupplierOptions = Boolean(
    isAdmin && !form.supplier_id && hasUnmatchedSupplierName(suppliers, newSupplierName),
  );

  // ── Side-by-side review derivations (review stage only) ──
  const reviewFiles = reviewPreviews;
  const openDeliveryNotes = useOpenDeliveryNotes({
    enabled: stage === 'review', supplierId: form.supplier_id, docType: form.doc_type, date: form.date,
  });
  const reviewIssues = stage === 'review' ? deriveReviewIssues({
    items, form,
    duplicateWarning: false, // upload flow blocks duplicates at save, no persisted flag
    supplierMissing: !form.supplier_id,
    recipientMismatch: recipientCheck?.match === 'mismatch',
    recipientName: recipientCheck?.recipient_name || '',
    canAddAlias: isAdmin,
    recipientDecidedBy: recipientCheck?.decided_by,
    recipientBusinessId: recipientCheck?.recipient_business_id || null,
    restaurantBusinessId: recipientCheck?.restaurant_business_id || null,
    supplierUsesDeliveryNotes: selectedSupplier ? !!selectedSupplier.uses_delivery_notes : undefined,
    canMarkDeliveryNotes: isAdmin,
    openDeliveryNotes,
    canConvertToMerkaz: isAdmin,
    agreedInvoiceDiscount: selectedSupplier?.fixed_invoice_discount,
    agreedInvoiceDiscountType: selectedSupplier?.fixed_invoice_discount_type || 'percent',
  }) : [];

  // Keyboard-first save: Ctrl/Cmd+S submits the review form. Routed through a
  // ref so the window listener registers once per review session but always
  // calls the latest handleSave closure.
  const saveShortcutRef = useRef(null);
  saveShortcutRef.current = handleSave;
  // The שולם note dialog owns the keyboard while it is open — same reasoning,
  // same ref-for-a-stale-closure trick, as DocumentViewPage's payNoteModalRef.
  // Without it, Ctrl+S while typing a note re-enters handleSave's raw save
  // path directly (skipping the dialog's own confirm button and its
  // `!payNote.trim()` gate the instant a partial note becomes non-empty).
  const payNoteModalRef = useRef(false);
  payNoteModalRef.current = payNoteModal;
  useEffect(() => {
    if (stage !== 'review') return;
    const onKey = (e) => {
      if (payNoteModalRef.current) return;
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        saveShortcutRef.current?.({ preventDefault: () => {} });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage]);

  return (
    <div>
      {confirmDialog}
      <h1 className="page-title">העלאת מסמך</h1>

      {/* Mode toggle — admin only. The bulk path saves every file as a draft
          for later review on /drafts; the single path is the established
          one-doc-with-optional-multi-page flow. */}
      {isAdmin && stage === 'upload' && (
        <div style={{ display: 'inline-flex', border: '1px solid #dde3ec', borderRadius: 7, overflow: 'hidden', marginBottom: 16 }}>
          {[
            { id: 'single', label: 'מסמך יחיד' },
            { id: 'bulk',   label: 'העלאה מרובה (טיוטות)' },
          ].map(m => (
            <button
              key={m.id}
              type="button"
              className="upload-mode-btn"
              onClick={() => setMode(m.id)}
              disabled={bulkProgress.running || processing}
              style={{
                padding: '6px 14px', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: '0.86rem', fontWeight: 600,
                background: mode === m.id ? '#0f3460' : 'transparent',
                color:      mode === m.id ? '#fff'    : 'var(--ink-muted)',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {mode === 'bulk' && stage === 'upload' && (
        <div className="card">
          <div
            className="dropzone"
            onDragOver={e => { e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); addBulkFiles(e.dataTransfer.files); }}
            onClick={() => !bulkProgress.running && bulkInputRef.current?.click()}
            style={{ cursor: bulkProgress.running ? 'not-allowed' : 'pointer', opacity: bulkProgress.running ? 'var(--disabled-opacity)' : 1 }}
          >
            {bulkProgress.running ? (
              <>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                <p className="dropzone-text">מעבד טיוטה {bulkProgress.current} מתוך {bulkProgress.total}...</p>
                <div style={{ maxWidth: 360, margin: '14px auto 0', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.round((bulkProgress.current / Math.max(1, bulkProgress.total)) * 100)}%`,
                    height: '100%', background: '#0f3460', transition: 'width 0.25s',
                  }} />
                </div>
              </>
            ) : (
              <>
                <Icon name="docs" size={40} className="dropzone-icon" strokeWidth={1.4} />
                <p className="dropzone-text">
                  {bulkStaged.length > 0 ? 'גרור קבצים נוספים או לחץ להוספה' : 'גרור קבצים לכאן או לחץ להעלאה'}
                </p>
                <p className="dropzone-hint">PDF, JPG, PNG · בין {BULK_MIN} ל-{BULK_MAX} קבצים · כל קובץ יישמר כטיוטה נפרדת</p>
              </>
            )}
          </div>
          <input ref={bulkInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
            onChange={e => { addBulkFiles(e.target.files); e.target.value = ''; }} />

          {bulkStaged.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {bulkStaged.map(({ file, previewUrl }, idx) => (
                  <div key={idx} style={{ position: 'relative', width: 96, height: 112, border: '2px solid #c5d5e8', borderRadius: 10, overflow: 'hidden', background: '#f7f9fc', flexShrink: 0 }}>
                    {previewUrl ? (
                      <img src={previewUrl} alt={file.name} style={{ width: '100%', height: '75%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: '75%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9BAAA2' }}><Icon name="documents" size={30} strokeWidth={1.4} /></div>
                    )}
                    <div style={{ height: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      <span style={{ fontSize: '0.62rem', color: '#666', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                        {file.name}
                      </span>
                    </div>
                    {!bulkProgress.running && (
                      <button
                        onClick={() => removeBulkFile(idx)}
                        style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                        <Icon name="close" size={10} strokeWidth={2.4} />
                      </button>
                    )}
                    <div style={{ position: 'absolute', bottom: 26, right: 4, background: '#0f3460', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: '0.6rem', fontWeight: 700 }}>
                      {idx + 1}
                    </div>
                  </div>
                ))}
                {bulkStaged.length < BULK_MAX && !bulkProgress.running && (
                  <div
                    onClick={() => bulkInputRef.current?.click()}
                    style={{ width: 96, height: 112, border: '2px dashed #b0c4de', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: '1.4rem' }}>+</span>
                    <span style={{ fontSize: '0.72rem' }}>הוסף קובץ</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="btn btn-primary"
                  onClick={processBulk}
                  disabled={bulkProgress.running || bulkStaged.length < BULK_MIN}
                >
                  {bulkProgress.running
                    ? `מעבד ${bulkProgress.current}/${bulkProgress.total}...`
                    : <><Icon name="sparkle" size={15} /> {`עבד ${bulkStaged.length} קבצים כטיוטות`}</>}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={clearBulkStaged} disabled={bulkProgress.running}>נקה</button>
              </div>
              {bulkStaged.length < BULK_MIN && (
                <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 8 }}>
                  הוסף לפחות {BULK_MIN - bulkStaged.length} קובץ נוסף כדי להפעיל מצב מרובה
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {mode === 'single' && stage === 'upload' && (
        <div className="card">
          <div
            className={`dropzone${dragging ? ' active' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current.click()}
          >
            {processing ? (
              <>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                <p className="dropzone-text">מעבד {stagedFiles.length > 1 ? `${stagedFiles.length} קבצים` : 'מסמך'} עם Claude AI...</p>
              </>
            ) : (
              <>
                <Icon name="folder" size={40} className="dropzone-icon" strokeWidth={1.4} />
                <p className="dropzone-text">
                  {stagedFiles.length > 0 ? 'גרור קבצים נוספים לכאן או לחץ להוספה' : 'גרור קבצים לכאן או לחץ להעלאה'}
                </p>
                <p className="dropzone-hint">PDF, JPG, PNG עד 20MB · עד 3 קבצים לאותו מסמך</p>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

          {/* Thumbnails */}
          {stagedFiles.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {stagedFiles.map(({ file, previewUrl }, idx) => (
                  <div key={idx} style={{ position: 'relative', width: 96, height: 112, border: '2px solid #c5d5e8', borderRadius: 10, overflow: 'hidden', background: '#f7f9fc', flexShrink: 0 }}>
                    {previewUrl ? (
                      <img src={previewUrl} alt={file.name} style={{ width: '100%', height: '75%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: '75%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9BAAA2' }}><Icon name="documents" size={30} strokeWidth={1.4} /></div>
                    )}
                    <div style={{ height: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      <span style={{ fontSize: '0.62rem', color: '#666', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                        {file.name}
                      </span>
                    </div>
                    <button
                      onClick={() => removeFile(idx)}
                      style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                      <Icon name="close" size={10} strokeWidth={2.4} />
                    </button>
                    <div style={{ position: 'absolute', bottom: 26, right: 4, background: '#0f3460', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: '0.6rem', fontWeight: 700 }}>
                      {idx + 1}
                    </div>
                  </div>
                ))}
                {stagedFiles.length < 3 && !processing && (
                  <div
                    onClick={() => fileInputRef.current.click()}
                    style={{ width: 96, height: 112, border: '2px dashed #b0c4de', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: '1.4rem' }}>+</span>
                    <span style={{ fontSize: '0.72rem' }}>הוסף קובץ</span>
                  </div>
                )}
              </div>
              {/* AI extraction toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', userSelect: 'none' }}>
                <div
                  onClick={() => setUseAI(v => !v)}
                  style={{
                    width: 40, height: 22, borderRadius: 11, position: 'relative', flexShrink: 0,
                    background: useAI ? '#0f3460' : '#d1d5db', transition: 'background 0.2s', cursor: 'pointer',
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 3, left: useAI ? 21 : 3,
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', display: 'block',
                  }} />
                </div>
                <span style={{ fontSize: '0.88rem', color: '#333', fontWeight: 500 }}>
                  <Icon name="sparkle" size={15} /> חלץ נתונים אוטומטית
                </span>
                <span style={{ fontSize: '0.78rem', color: '#aaa' }}>
                  {useAI ? 'Claude AI יחלץ את הנתונים' : 'מילוי ידני בלבד'}
                </span>
              </label>

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={processFiles} disabled={processing}>
                  {processing
                    ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: 'inline-block', verticalAlign: 'middle', marginLeft: 6 }} />מעבד...</>
                    : useAI
                      ? <><Icon name="sparkle" size={15} /> {`עבד ${stagedFiles.length > 1 ? stagedFiles.length + ' קבצים' : 'מסמך'}`}</>
                      : <><Icon name="upload" size={15} /> העלה קובץ</>
                  }
                </button>
                <button className="btn btn-secondary btn-sm" onClick={clearStaged} disabled={processing}>נקה</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 16, color: 'var(--ink-muted)' }}>או הזן ידנית</h3>
            <button className="btn btn-outline" onClick={() => setStage('review')}>הזנה ידנית <Icon name="chevron-inline" size={14} /></button>
          </div>
        </div>
      )}

      {stage === 'review' && (
        <form onSubmit={handleSave}>
          {/* Recipient mismatch is folded into the issue strip below as a
              warning chip (single source of truth), so no separate banner here. */}
          {ocrRawText && (
            <div style={{ background:'#fff8f0', border:'1px solid #fde8c8', borderRadius:12, padding:'14px 18px', marginBottom:16 }}>
              <div style={{ fontWeight:700, color:'#b45309', marginBottom:8, fontSize:'0.9rem' }}>
                <Icon name="alert" size={15} /> לא ניתן היה לנתח את המסמך אוטומטית — אנא מלא את הפרטים ידנית
              </div>
              <details>
                <summary style={{ cursor:'pointer', fontSize:'0.82rem', color:'#666', userSelect:'none' }}>הצג טקסט גולמי מ-Claude</summary>
                <pre style={{ marginTop:8, fontSize:'0.75rem', color:'#444', background:'#f7f9fc', borderRadius:8, padding:'10px 12px', overflowX:'auto', whiteSpace:'pre-wrap', wordBreak:'break-word', maxHeight:200, overflowY:'auto' }}>
                  {ocrRawText}
                </pre>
              </details>
            </div>
          )}
          {cutLineCount != null && (
            <div style={{ background:'#fff8f0', border:'1px solid #fde8c8', borderRadius:12, padding:'12px 18px', marginBottom:16 }}>
              <span style={{ color:'#b45309', fontWeight:600, fontSize:'0.9rem' }}>
                <Icon name="alert" size={15} /> המסמך ארוך מדי לקריאה אוטומטית מלאה — נקראו {cutLineCount} שורות ראשונות. השלם את השורות החסרות ידנית לפני השמירה.
              </span>
            </div>
          )}
          {openCredits?.cnt > 0 && (
            <div style={{ background:'#fffbe6', border:'1px solid #fde8c8', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <span style={{ color:'#b45309', fontWeight:600, fontSize:'0.9rem' }}>
                <Icon name="alert" size={15} /> לספק זה יש {openCredits.cnt} זיכויים פתוחים בסך {formatCurrency(openCredits.total)}
              </span>
              <Link to="/credits" style={{ color:'#0f3460', fontSize:'0.85rem', fontWeight:600, textDecoration:'underline' }}>צפה בזיכויים <Icon name="chevron-inline" size={13} /></Link>
            </div>
          )}
          {/* Sticky issue strip, metadata as a compact full-width bar, then
              the document ⟷ items split. */}
          <ReviewIssueSummary
            issues={reviewIssues}
            onAction={a => {
              if (a.kind === 'add-alias') {
                confirmAddRecipientAlias({
                  askConfirm, restaurantId, name: a.name,
                  docBusinessId: recipientCheck?.recipient_business_id || null,
                  onAdded: () => setRecipientCheck(c => (c ? { ...c, match: 'match' } : c)),
                });
              } else if (a.kind === 'ignore-bid') {
                confirmIgnoreRecipientBid({
                  askConfirm, restaurantId, bid: a.bid, name: a.name,
                  onResult: r => setRecipientCheck(c => (c ? { ...c, match: r.match, decided_by: r.decided_by } : c)),
                });
              } else if (a.kind === 'mark-delivery-notes') {
                markSupplierDeliveryNotes(form.supplier_id, setSuppliers);
              } else if (a.kind === 'convert-to-merkaz') {
                // Form only — saving/approving then opens the link modal through the
                // existing מרכזת path; the chip disappears as the type no longer qualifies.
                setForm(f => ({ ...f, doc_type: 'חשבונית מרכזת' }));
                setConvertRange(openNotesRange(openDeliveryNotes, form.supplier_id, form.date));
                toast('סוג המסמך שונה לחשבונית מרכזת — בשמירה ייפתח חלון קישור התעודות');
              }
            }}
          />
          <div className="card review-meta-bar">
            <h3 className="modal-title" style={{ marginBottom: 10 }}>פרטי המסמך</h3>
            <div className="ri-meta-row">
              <div className="form-group ri-meta-supplier">
                <label>ספק *</label>
                <SearchableSelect
                    id="ri-supplier"
                    ariaLabel="ספק"
                    value={form.supplier_id}
                    onChange={v => handleSupplierChange(v)}
                    options={supplierOptions}
                    placeholder="חפש או בחר ספק…"
                    initialQuery={form.supplier_id ? '' : newSupplierName}
                    onQueryChange={setNewSupplierName}
                    createLabel={q => `הוסף ספק חדש: "${q}"`}
                    onCreate={async (name) => {
                      try {
                        const r = await suppliersApi.create({ name, business_id: printedSupplierBid, ...newSupplierOptions });
                        track(EVENTS.SUPPLIER_CREATED_INLINE, { context: 'upload_review', from_unmatched: !!newSupplierName });
                        const fresh = await suppliersApi.getAll({ include_empty: true });
                        setSuppliers(fresh.data);
                        handleSupplierChange(String(r.data.id));
                        setNewSupplierName('');
                        setNewSupplierOptions({ hq_only: false, category_line_id: '' });
                        toast(r.data.created === false ? 'הספק כבר קיים — נבחר' : 'ספק נוצר');
                      } catch (e) {
                        toast(e.response?.data?.error || 'שגיאה ביצירת ספק', 'error');
                      }
                    }}
                  />
                {showNewSupplierOptions && (
                  <NewSupplierOptions
                    compact
                    name={newSupplierName}
                    businessId={printedSupplierBid}
                    hqOnly={newSupplierOptions.hq_only}
                    onHqOnlyChange={hq_only => setNewSupplierOptions(current => ({ ...current, hq_only }))}
                    categoryLineId={newSupplierOptions.category_line_id}
                    onCategoryLineChange={category_line_id => setNewSupplierOptions(current => ({ ...current, category_line_id }))}
                  />
                )}
                {!form.supplier_id && newSupplierName && (
                  <p style={{ fontSize: '0.78rem', color: '#b45309', marginTop: 6 }}>
                    לא נמצא ספק תואם ל"{newSupplierName}". בחר ספק קיים או הוסף חדש.
                  </p>
                )}
              </div>
              <div className="form-group ri-meta-doctype">
                <label>סוג מסמך</label>
                <SearchableSelect
                  id="ri-doc-type"
                  ariaLabel="סוג מסמך"
                  value={form.doc_type}
                  onChange={v => setForm(f => ({ ...f, doc_type: v }))}
                  options={DOC_TYPE_OPTIONS}
                  placeholder="חפש סוג מסמך…"
                />
              </div>
              <div className="form-group ri-meta-docnum">
                <label>מספר מסמך</label>
                <input id="ri-doc_num" className="form-control" value={form.doc_num} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, doc_num: v })); }} placeholder="INV-001" />
              </div>
              <div className="form-group ri-meta-date">
                <label>תאריך</label>
                <input id="ri-date" className="form-control" value={form.date} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, date: v })); }} placeholder="DD/MM/YYYY" />
                {isImplausibleDateYear(form.date) && (
                  <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 4 }}>
                    <Icon name="alert" size={15} /> השנה בתאריך ({form.date.trim().slice(-4)}) חריגה — לא ניתן לשמור עד שתתוקן
                  </div>
                )}
              </div>
              <div className="form-group ri-meta-status">
                <label>סטטוס</label>
                <SearchableSelect
                  ariaLabel="סטטוס"
                  value={form.status}
                  onChange={v => setForm(f => ({ ...f, status: v }))}
                  options={STATUS_OPTIONS}
                />
              </div>
            </div>
          </div>

          <div className="review-split">
          <ReviewDocPane files={reviewFiles} />
          <div className="review-split-fields">
          <div className="card review-items-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>פריטים</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-sm btn-secondary" onClick={recalcTotals}>חשב סכומים</button>
                <button type="button" className="btn btn-sm btn-outline" onClick={addItem}>+ הוסף שורה</button>
              </div>
            </div>
            <div className="table-wrapper">
              <table className="items-table-edit">
                <thead>
                  <tr>
                    <th style={{ width: '12%' }}>מק"ט</th>
                    <th style={{ width: '19%' }}>תיאור</th>
                    <th style={{ width: '6%'  }}>כמות</th>
                    <th style={{ width: '7%'  }}>מחיר ליחידה</th>
                    <th style={{ width: '7%'  }}>הנחה</th>
                    <th style={{ width: '7%'  }}>מחיר נטו</th>
                    {hasDeposit     && <th style={{ width: '7%', background: '#f0f9ff' }}>פיקדון</th>}
                    {hasPurchaseTax && <th style={{ width: '7%', background: '#fdf4ff' }}>מס קנייה</th>}
                    <th style={{ width: '7%'  }}>סך הכל</th>
                    {/* Pinned to the same PIXEL width (a % alone is only a hint
                        under auto layout — each column sizes to its widest cell's
                        content, and the longer label was winning that negotiation),
                        both wrapped onto two lines short enough to stay under it. */}
                    <th style={{ width: 90, background: '#fffbe6' }}>
                      <span style={{ display: 'inline-block', maxWidth: 78, whiteSpace: 'normal', lineHeight: 1.2 }}>כמות לזיכוי</span>
                    </th>
                    <th style={{ width: 90, background: '#fffbe6' }}>
                      <span style={{ display: 'inline-block', maxWidth: 78, whiteSpace: 'normal', lineHeight: 1.2 }}>הפרש מחיר לזיכוי</span>
                    </th>
                    <th style={{ width: '7%', background: '#fffbe6' }}>סך לזיכוי</th>
                    <th style={{ width: '6%'  }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const isPct = it.discount_type !== 'amount';
                    const net = calcNetUnitPrice(it);
                    const hasCredit = (parseFloat(it.credit_qty) || 0) > 0 || (parseFloat(it.credit_price_diff) || 0) > 0;
                    // Shared row analysis (utils/reviewIssues) — same computation
                    // as DocumentViewPage edit mode and the issue strip, so the
                    // inline flags can never diverge between the two screens.
                    // When the supplier overcharged vs. the fixed price, the
                    // overcharge button below offers a one-click credit on the
                    // per-unit gap (credit_price_diff × qty → 'סך לזיכוי').
                    const {
                      mismatched, totalWarn, expectedTotal, curPrice, anchorPrice, anchorIsFixed,
                      priceChanged, overcharge, creditDiffSet,
                    } = analyzeReviewRow(it);
                    return (
                      <tr key={it._key ?? i} style={{ background: hasCredit ? '#fffbe6' : undefined }}>
                        <td><input value={it.sku || ''} onChange={e => updateItem(i, 'sku', e.target.value)} placeholder="—" /></td>
                        <td><input value={it.description} onChange={e => updateItem(i, 'description', e.target.value)} /></td>
                        <td><input type="number" step="any" value={it.qty} onChange={e => updateItem(i, 'qty', e.target.value)} /></td>
                        <td style={{ verticalAlign: 'top' }}>
                          <input
                            id={`ri-item-${i}-unit_price`}
                            type="number"
                            step="any"
                            value={it.unit_price}
                            onChange={e => updateItem(i, 'unit_price', e.target.value)}
                            title={priceChanged ? (anchorIsFixed ? `מחיר קבוע בקטלוג: ${formatCurrency(anchorPrice)}` : `מחיר קודם בקטלוג: ${formatCurrency(anchorPrice)}`) : undefined}
                            style={priceChanged
                              ? { border: '2px solid #f97316', background: '#fff7ed', borderRadius: 4 }
                              : undefined}
                          />
                          {priceChanged && (
                            <div
                              title={anchorIsFixed
                                ? `מחיר קבוע בקטלוג: ${formatCurrency(anchorPrice)} • עכשיו: ${formatCurrency(curPrice)}`
                                : `שינוי מחיר — היה ${formatCurrency(anchorPrice)}, עכשיו ${formatCurrency(curPrice)}`}
                              style={{ fontSize: '0.68rem', color: '#c2410c', fontWeight: 600, marginTop: 3, lineHeight: 1.2 }}
                            >
                              {anchorIsFixed ? 'קבוע ' : 'היה '}
                              {formatCurrency(anchorPrice)} <Icon name="chevron-inline" size={11} /> {formatCurrency(curPrice)}
                            </div>
                          )}
                          <PriceSourceBadge item={it} />
                          {overcharge > 0 && !creditDiffSet && (
                            <button
                              type="button"
                              onClick={() => updateItem(i, 'credit_price_diff', overcharge)}
                              style={{ marginTop: 4, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: '#dc2626', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              title={`מילוי אוטומטי של 'הפרש מחיר לזיכוי' בערך ${formatCurrency(overcharge)} ליחידה. הסכום הכולל יהיה ${formatCurrency(overcharge * (parseFloat(it.qty) || 0))}.`}
                            >
                              פתח זיכוי על ההפרש ({formatCurrency(overcharge)}/יח')
                            </button>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <input id={`ri-item-${i}-discount`} type="number" step="any" min="0" value={it.discount ?? 0}
                              onChange={e => updateItem(i, 'discount', e.target.value)} style={{ flex: 1, minWidth: 0 }} />
                            <button type="button" onClick={() => updateItem(i, 'discount_type', isPct ? 'amount' : 'percent')}
                              style={{ padding: '3px 6px', fontSize: '0.78rem', fontWeight: 700, border: '1px solid #b0c4de', borderRadius: 4, background: isPct ? '#e8f0fe' : '#fff3e0', color: isPct ? '#1a56db' : '#b45309', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {isPct ? '%' : '₪'}
                            </button>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', color: '#333', fontSize: '0.88rem', fontWeight: 500 }}>{formatCurrency(net)}</td>
                        {hasDeposit && (
                          <td style={{ background: '#f0f9ff' }}>
                            <input type="number" step="any" min="0" value={it.deposit ?? 0}
                              onChange={e => updateItem(i, 'deposit', e.target.value)}
                              style={{ background: 'transparent' }} />
                          </td>
                        )}
                        {hasPurchaseTax && (
                          <td style={{ background: '#fdf4ff' }}>
                            <input type="number" step="any" min="0" value={it.purchase_tax ?? 0}
                              onChange={e => updateItem(i, 'purchase_tax', e.target.value)}
                              style={{ background: 'transparent' }} />
                          </td>
                        )}
                        <td style={{ verticalAlign: 'top' }}>
                          <input
                            id={`ri-item-${i}-line_total`}
                            type="number"
                            step="any"
                            value={it.line_total}
                            onChange={e => updateItem(i, 'line_total', e.target.value)}
                            title={mismatched || totalWarn ? `כמות × מחיר נטו = ${formatCurrency(expectedTotal)}` : undefined}
                            style={mismatched
                              ? { border: '2px solid #dc2626', background: '#fef2f2', borderRadius: 4 }
                              : totalWarn
                                ? { border: '2px solid #f59e0b', background: '#fffbeb', borderRadius: 4 }
                                : undefined}
                          />
                          {(mismatched || totalWarn) && (
                            <div style={{ fontSize: '0.72rem', color: mismatched ? '#b91c1c' : '#b45309', fontWeight: 600, marginTop: 3, whiteSpace: 'nowrap' }}>
                              צפוי: {formatCurrency(expectedTotal)}
                            </div>
                          )}
                        </td>
                        <td style={{ background: '#fffde8' }}>
                          <input type="number" step="any" min="0" value={it.credit_qty ?? 0}
                            onChange={e => updateItem(i, 'credit_qty', e.target.value)} style={{ background: 'transparent' }} />
                        </td>
                        <td style={{ background: '#fffde8' }}>
                          <input type="number" step="any" min="0" value={it.credit_price_diff ?? 0}
                            onChange={e => updateItem(i, 'credit_price_diff', e.target.value)} style={{ background: 'transparent' }}
                            title="הפרש מחיר לכל יחידה. סך הזיכוי = הפרש × כמות. שימושי כשהספק חייב יותר מהמחיר המוסכם." />
                        </td>
                        <td style={{ background: '#fffde8', textAlign: 'center', fontSize: '0.88rem', color: hasCredit ? '#b45309' : '#ccc', fontWeight: hasCredit ? 600 : 400 }}>
                          {hasCredit ? formatCurrency(it.credit_total) : '—'}
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => removeItem(i)}
                            aria-label={`מחק שורה: ${it.description || 'ללא תיאור'}`} title="מחק שורה">
                            <Icon name="trash" size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {items.length === 0 && <div className="empty-state" style={{ padding: '24px' }}><p>אין פריטים. לחץ "הוסף שורה"</p></div>}

            <div className="totals-box" style={{ marginTop: 20 }}>
              <div className="total-row">
                <span>סכום לפני מע"מ</span>
                <span style={{ width: 120, textAlign: 'left', direction: 'ltr',
                  border: '1px solid #e8ecf0', borderRadius: 6, padding: '4px 8px',
                  background: '#f7f9fc', color: 'var(--ink-muted)', display: 'inline-block' }}>
                  {form.subtotal !== '' && form.subtotal != null
                    ? Number(form.subtotal).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : ''}
                </span>
              </div>
              {(() => {
                const discountAmount = calcDiscountAmount(form.subtotal, form.invoice_discount, form.invoice_discount_type);
                // Fixed-invoice-discount mismatch alert: mirrors the per-item
                // fixed_price / fixed_discount red-border pattern. When the
                // supplier has an agreed fixed discount but the document's
                // discount differs (value or type), flag it in orange so the
                // user notices the supplier didn't honor the agreement.
                const supFixed     = parseFloat(selectedSupplier?.fixed_invoice_discount) || 0;
                const supFixedType = selectedSupplier?.fixed_invoice_discount_type || 'percent';
                // Shared predicate (utils/reviewIssues) — same check drives the
                // consolidated issue strip, so the two can't diverge.
                const invDiscMismatch = invoiceDiscountMismatch({
                  agreed: supFixed,
                  agreedType: supFixedType,
                  current: form.invoice_discount,
                  currentType: form.invoice_discount_type,
                });
                return (
                  <>
                    <div className="total-row" style={{ alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>הנחה</span>
                        <div style={{ display: 'inline-flex', border: '1px solid #dde3ec', borderRadius: 5, overflow: 'hidden' }}>
                          {[
                            { id: 'percent', label: '%' },
                            { id: 'amount',  label: '₪' },
                          ].map(opt => (
                            <button key={opt.id} type="button"
                              onClick={() => handleInvoiceDiscount({ invoice_discount_type: opt.id })}
                              style={{ padding: '2px 9px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                                fontSize: '0.74rem', fontWeight: 600,
                                background: form.invoice_discount_type === opt.id ? '#0f3460' : 'transparent',
                                color:      form.invoice_discount_type === opt.id ? '#fff'    : '#777' }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <input id="ri-invoice_discount" type="number" step="any" value={form.invoice_discount ?? 0}
                        onChange={e => handleInvoiceDiscount({ invoice_discount: e.target.value })}
                        style={{ width: 120,
                          border: invDiscMismatch ? '2px solid #f97316' : '1px solid #ddd',
                          borderRadius: 6, padding: '4px 8px', textAlign: 'left', direction: 'ltr',
                          background: invDiscMismatch ? '#fff7ed' : '#fff' }} />
                    </div>
                    {invDiscMismatch && (
                      <div className="total-row" style={{ paddingTop: 0, marginTop: -4 }}>
                        <span style={{ color: '#c2410c', fontSize: '0.78rem' }}>
                          <Icon name="alert" size={13} /> הנחה קבועה מוסכמת לספק: {supFixed}{supFixedType === 'percent' ? '%' : ' ₪'} — לא תואם למסמך
                        </span>
                      </div>
                    )}
                    {hasInvoiceDiscount(discountAmount) && (
                      <div className="total-row">
                        <span style={{ color: '#b45309' }}>סכום ההנחה</span>
                        <span style={{ width: 120, textAlign: 'left', direction: 'ltr',
                          border: '1px solid #e8ecf0', borderRadius: 6, padding: '4px 8px',
                          background: '#fff8f0', color: '#b45309', fontWeight: 600, display: 'inline-block' }}>
                          {signedDiscount(discountAmount)}
                        </span>
                      </div>
                    )}
                    {hasInvoiceDiscount(discountAmount) && (
                      <div className="total-row" style={{ borderTop: '1px solid #eef1f5', paddingTop: 6 }}>
                        <span>סה"כ לפני מע"מ אחרי הנחה</span>
                        <span style={{ width: 120, textAlign: 'left', direction: 'ltr',
                          border: '1px solid #e8ecf0', borderRadius: 6, padding: '4px 8px',
                          background: '#f7f9fc', color: 'var(--ink-muted)', display: 'inline-block' }}>
                          {Number((parseFloat(form.subtotal) || 0) - discountAmount)
                            .toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="total-row" style={{ alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>מע"מ</span>
                  <div style={{ display: 'inline-flex', border: '1px solid #dde3ec', borderRadius: 5, overflow: 'hidden' }}>
                    {['auto', 'manual'].map(m => (
                      <button key={m} type="button" onClick={() => setVatMode(m)}
                        style={{ padding: '2px 9px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                          fontSize: '0.74rem', fontWeight: 600,
                          background: vatMode === m ? '#0f3460' : 'transparent',
                          color: vatMode === m ? '#fff' : '#777' }}>
                        {m === 'auto' ? `אוטו (${vatRate}%)` : 'ידני ₪'}
                      </button>
                    ))}
                  </div>
                </div>
                {vatMode === 'auto' ? (
                  <span style={{ width: 120, textAlign: 'left', direction: 'ltr',
                    border: '1px solid #e8ecf0', borderRadius: 6, padding: '4px 8px',
                    background: '#f7f9fc', color: 'var(--ink-muted)', display: 'inline-block' }}>
                    {form.vat !== '' ? Number(form.vat).toLocaleString('he-IL', { minimumFractionDigits: 2 }) : ''}
                  </span>
                ) : (
                  <input type="number" step="any" min="0" value={form.vat}
                    onChange={e => handleManualVat(e.target.value)}
                    style={{ width: 120, border: '1.5px solid #0f3460', borderRadius: 6,
                      padding: '4px 8px', textAlign: 'left', direction: 'ltr' }} />
                )}
              </div>
              <div className="total-row grand">
                <span>סה"כ לתשלום</span>
                <span style={{ width: 120, textAlign: 'left', direction: 'ltr', fontWeight: 700,
                  border: '1px solid #e8ecf0', borderRadius: 6, padding: '4px 8px',
                  background: '#f7f9fc', display: 'inline-block' }}>
                  {form.total !== '' ? Number(form.total).toLocaleString('he-IL', { minimumFractionDigits: 2 }) : ''}
                </span>
              </div>
              {/* "שולם" — same control and meaning as DocumentViewPage's copy
                  on a draft's edit form: ticking it makes "שמור מסמך" save the
                  document ALREADY approved and records the payment in the
                  same action, instead of leaving payment for a later trip
                  through /drafts. Needs a supplier; method defaults to the
                  supplier's, date to today. */}
              {isAdmin && paymentTrackingEnabled && isPayableDocType(form.doc_type) && !autoPaysOnApproval() && (
                <div className="ri-pay-on-approve" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: form.supplier_id ? 'pointer' : 'default', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={payOnApprove.checked}
                      disabled={!form.supplier_id}
                      onChange={e => setPayOnApprove(p => ({
                        ...p, checked: e.target.checked,
                        payment_method: p.payment_method || selectedSupplierMethod() || PAYMENT_METHODS[0],
                        payment_date: p.payment_date || todayISO(),
                      }))}
                    />
                    <span>שולם</span>
                    {!form.supplier_id && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', fontWeight: 400 }}>— יש לבחור ספק תחילה</span>
                    )}
                  </label>
                  {payOnApprove.checked && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
                      <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
                        <label htmlFor="ri-pay-method">אמצעי תשלום</label>
                        <select id="ri-pay-method" className="form-control" value={payOnApprove.payment_method}
                          onChange={e => setPayOnApprove(p => ({ ...p, payment_method: e.target.value }))}>
                          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label htmlFor="ri-pay-date">תאריך תשלום</label>
                        <input id="ri-pay-date" type="date" className="form-control" value={payOnApprove.payment_date}
                          onChange={e => setPayOnApprove(p => ({ ...p, payment_date: e.target.value }))} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
          </div>

          <div className="review-actions-sticky" style={{ display: 'flex', gap: 12 }}>
            {/* The visible "שומר…" state matters as much as the disable: a
                button that looks idle while the request is in flight is exactly
                what makes people click again. */}
            <button type="submit" className="btn btn-success" disabled={saving || isImplausibleDateYear(form.date)}
              title="Ctrl+S / ⌘S"><Icon name="save" size={16} /> {saving ? 'שומר…' : 'שמור מסמך'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => {
              track(EVENTS.REVIEW_ABANDONED, { exit_via: 'back_to_upload', had_edits: editedItemRowsRef.current.size > 0, rows_edited_count: editedItemRowsRef.current.size });
              setCutLineCount(null); // "הזנה ידנית" next must not inherit this document's note
              setStage('upload');
            }}><Icon name="chevron-inline" size={14} flip /> חזור להעלאה</button>
            <button type="button" className="btn btn-secondary" onClick={() => {
              track(EVENTS.REVIEW_ABANDONED, { exit_via: 'cancel', had_edits: editedItemRowsRef.current.size > 0, rows_edited_count: editedItemRowsRef.current.size });
              navigate(-1);
            }}>ביטול</button>
          </div>
        </form>
      )}

      {payNoteModal && (
        <Modal onClose={() => setPayNoteModal(false)} size={440} ariaLabel="המסמך מסומן כשולם">
          <h2 id="pay-note-title" className="modal-title">המסמך מסומן כשולם</h2>
          <p style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', marginTop: 0 }}>
            תעדי מדוע המסמך נחשב שולם. ההערה תישמר על המסמך, על שמך, ותוצג לכל המשתמשים.
          </p>
          <div className="reason-chips" style={{ marginBottom: 10 }}>
            <button type="button" className="reason-chip" onClick={() => setPayNote(PAY_NOTE_QUICK)}>
              {PAY_NOTE_QUICK}
            </button>
          </div>
          <div className="form-group">
            <label htmlFor="pay-note-text">הערה</label>
            <textarea id="pay-note-text" className="form-control" rows={3} value={payNote}
              onChange={e => setPayNote(e.target.value)} placeholder="למשל: הופיע שולם על המסמך" />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setPayNoteModal(false)}>ביטול</button>
            <button type="button" className="btn btn-success" disabled={!payNote.trim() || saving}
              onClick={() => { setPayNoteModal(false); handleSave(); }}>
              <Icon name="check" size={16} /> שמור מסמך
            </button>
          </div>
        </Modal>
      )}

      {linkModal && (
        <LinkDeliveryNotesModal
          invoiceId={linkModal.invoiceId}
          invoiceDocNum={linkModal.invoiceDocNum}
          invoiceTotal={linkModal.invoiceTotal}
          invoiceSubtotal={linkModal.invoiceSubtotal}
          supplierName={linkModal.supplierName}
          invoiceStatus={linkModal.invoiceStatus}
          existingLinkCount={linkModal.existingLinkCount}
          initialRange={linkModal.initialRange || null}
          files={reviewFiles}
          onClose={() => {
            const after = linkModal.afterClose;
            setLinkModal(null);
            after?.();
          }}
        />
      )}

      {openCreditsModal && (
        <LinkOpenCreditsModal
          creditDocId={openCreditsModal.creditDocId}
          creditDocTotal={openCreditsModal.creditDocTotal}
          creditDocSubtotal={openCreditsModal.creditDocSubtotal}
          creditDocVat={openCreditsModal.creditDocVat}
          supplierId={openCreditsModal.supplierId}
          supplierName={openCreditsModal.supplierName}
          onClose={() => {
            const after = openCreditsModal.afterClose;
            setOpenCreditsModal(null);
            after?.();
          }}
        />
      )}

      {learnRuleModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => learnRuleModal.onDecline?.()}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: '24px 28px',
              maxWidth: 480, width: '92%', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
              direction: 'rtl', textAlign: 'right',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, color: '#2c3e50' }}>
              ללמוד כלל חדש לספק?
            </h3>
            <p style={{ marginBottom: 12, color: '#444', lineHeight: 1.5 }}>
              {learnRuleModal.rule?.prompt || 'זוהה דפוס תיקון. לשמור כלל לספק זה?'}
            </p>
            <div style={{
              background: '#f6f8fa', border: '1px solid #e1e4e8',
              borderRadius: 6, padding: '10px 12px', marginBottom: 16,
              fontSize: 14, color: '#333',
            }}>
              {learnRuleModal.rule?.text}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
              <button className="btn btn-primary" onClick={() => learnRuleModal.onConfirm?.()}>
                כן, שמור כלל
              </button>
              <button className="btn btn-secondary" onClick={() => learnRuleModal.onDecline?.()}>
                לא תודה
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
